import type { TextFieldProps } from '@material-ui/core';
import { Box, TextField as TextFieldCore, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import textFieldStyles from 'styles/textField';

export interface ExtendedTextFieldProps
  extends Omit<TextFieldProps, 'variant' | 'tooltip'> {
  variant?: 'standard' | 'filled' | 'outlined' | undefined;
  tooltip?: string;
  readonly?: boolean;
}

const TextField: FunctionComponent<ExtendedTextFieldProps> = ({
  label,
  variant,
  required,
  tooltip,
  helperText,
  placeholder,
  readonly,
  disabled,
  ...props
}: ExtendedTextFieldProps) => {
  const classes = textFieldStyles();
  const { dir } = useSelector((state: $Lns.DefaultState) => state.default);
  const direction = dir ?? i18n.dir();

  const textFieldComponent = (
    <TextFieldCore
      {...props}
      className={clsx(classes.textField, [variant, direction, props.className])}
      InputProps={{
        className: clsx(classes.input, {
          [classes.disabledInput]: disabled
        }),
        disableUnderline: true
      }}
      placeholder={placeholder}
      disabled={readonly || disabled}
    />
  );

  return (
    <Box className={direction}>
      {label && (
        <div className={classes.label}>
          {label}
          {required && <span className={classes.required}>*</span>}
        </div>
      )}
      {tooltip ? (
        <Tooltip placement="top-start" title={tooltip}>
          <span>{textFieldComponent}</span>
        </Tooltip>
      ) : (
        textFieldComponent
      )}
      {helperText && (
        <div
          className={clsx(classes.helperText, {
            [classes.error]: !!props.error
          })}
        >
          {helperText}
        </div>
      )}
    </Box>
  );
};

export default TextField;

TextField.defaultProps = {
  variant: 'outlined',
  tooltip: undefined,
  readonly: false
};
